import React, { FC, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useForm, useFormState } from "react-final-form";
import UploadFile from "../../../../../components/UploadFile/UploadFile";
import { IFile } from "../../../../../components/UploadFile/types";
import { NotifyMode } from "../../../../notify/NotifyModeEnum";
import { mutateErrors } from "../../../../../utils";
import { useNotify } from "../../../../notify/useNotify";
import { ICourseComponentProps } from "../../../types";
import useLanguageCourseQueries from "../../../hooks/useLanguageCourseQueries";

const useStyles = makeStyles(() => ({
  fileUploader: {
    marginTop: 16,
  },
}));

const DetailImages: FC<ICourseComponentProps> = ({ fieldsGroupName }) => {
  const classes = useStyles();

  const { values } = useFormState();
  const {
    mutators: { push, remove },
  } = useForm();
  const snackNotify = useNotify();
  const { uploadDetailFile } = useLanguageCourseQueries();

  const detailFiles: IFile[] = useMemo(
    () => values?.[fieldsGroupName]?.detailFiles || [],
    [fieldsGroupName, values],
  );

  const addNewFiles = useCallback(
    async (files) => {
      const succeededFileNames: string[] = [];
      const errorMessages: string[] = [];

      for (const file of files) {
        try {
          let { data } = await uploadDetailFile(file);
          push(`${fieldsGroupName}.detailFiles`, data);
          succeededFileNames.push(file.name);
        } catch (e) {
          const { message } = mutateErrors(e);
          errorMessages.push(`${file.name} - ${message}`);
        }
      }

      if (errorMessages.length > 0) {
        snackNotify("Error", errorMessages.join("\n"), NotifyMode.ERROR);
        return;
      }

      const successMessageEnding =
        succeededFileNames.length > 1
          ? "\nfiles have been uploaded."
          : " has been uploaded.";

      snackNotify(
        "Success",
        succeededFileNames.join("\n") + successMessageEnding,
        NotifyMode.SUCCESS,
      );
    },
    [fieldsGroupName],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const removeFileHandler = useCallback(
    (file) => {
      const index = detailFiles.indexOf(file);

      remove(`${fieldsGroupName}.detailFiles`, index);
    },
    [detailFiles, fieldsGroupName, remove],
  );

  return (
    <div className={classes.fileUploader}>
      <UploadFile
        uploadedFiles={detailFiles}
        addNewFiles={addNewFiles}
        removeFile={removeFileHandler}
        titleText="Upload Documents/Flyers"
        allowedFileTypes={["pdf", "jpg", "jpeg", "png"]}
        maxSize={5}
        maxFiles={5}
      />
    </div>
  );
};

export default DetailImages;
